import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import {
  IconDatabase,
  IconFolder,
  IconDownload,
  IconTrash,
  IconRefresh,
  IconAlert,
  IconCheck,
  IconClock,
  IconLock,
  IconMail,
  IconShieldCheck,
} from "../icons";

/**
 * BACKUP — a cópia de segurança de tudo, e a régua dela.
 *
 * A tela responde, de cima para baixo, as três perguntas de quem cuida disso:
 * "a última cópia saiu?", "o que exatamente entra nela?" e "como eu pego uma
 * cópia de volta?". A configuração fica embaixo, escondida atrás de um botão:
 * é o que se mexe uma vez e não se olha mais.
 *
 * O motor está em server/backup.ts — inclusive a descoberta dos bancos pelos
 * `.env`, que é o que faz esta tela mostrar sistema novo sem ninguém cadastrar
 * nada aqui.
 */

interface Parte {
  tipo: "banco" | "pasta" | "config";
  nome: string;
  arquivo: string;
  bytes: number;
  ok: boolean;
  erro?: string;
}

interface Copia {
  id: number;
  janela: string | null;
  origem: string;
  status: string;
  pasta: string | null;
  partes: Parte[];
  tamanho_bytes: string | number;
  erro: string | null;
  email_status: string | null;
  criado_por: string | null;
  iniciado_em: string;
  terminado_em: string | null;
  removido_em: string | null;
  removido_por: string | null;
}

interface Config {
  ativo: boolean;
  cadaHoras: number;
  destino: string;
  incluirBancos: boolean;
  incluirDocumentos: boolean;
  incluirConfig: boolean;
  pastasExtras: string;
  manterRecentes: number;
  manterDias: number;
  manterSemanas: number;
  emailQuando: string;
  emailPara: string;
  emailHora: string;
  emailAnexar: boolean;
  emailLimiteMb: number;
  emailFalha: boolean;
  pgDump: string;
  ultimoErro: string | null;
  atualizadoPor: string | null;
  atualizadoEm: string | null;
}

interface Retrato {
  config: Config;
  destinoPadrao: string;
  instalacao: string;
  ultimo: {
    id: number;
    status: string;
    tamanho_bytes: string | number;
    iniciado_em: string;
    terminado_em: string | null;
    origem: string;
    erro: string | null;
  } | null;
  guardado: { copias: number; bytes: number };
  proxima: string | null;
  rodando: boolean;
}

interface Alvos {
  bancos: { nome: string; host: string; porta: number; sistemas: string[] }[];
  pastas: { nome: string; caminho: string; sobre: string; bytes: number }[];
  comercial: string[];
  pgDump: string | null;
  pgDumpOk: boolean;
}

/* ------------------------------------------------------------------ *
 *  Formatos                                                            *
 * ------------------------------------------------------------------ */

function bytes(n: number | string | null | undefined): string {
  const v = Number(n ?? 0);
  if (!v) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(v) / Math.log(1024)));
  return (v / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1).replace(".", ",") + " " + u[i];
}

function quando(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function relativo(iso: string | null): string {
  if (!iso) return "";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "agora";
  if (min < 60) return "há " + min + " min";
  const h = Math.round(min / 60);
  if (h < 24) return "há " + h + " h";
  const d = Math.round(h / 24);
  return "há " + d + (d === 1 ? " dia" : " dias");
}

/** Quanto falta para a próxima cópia automática. */
function daqui(iso: string | null): string {
  if (!iso) return "desligado";
  const min = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (min <= 0) return "a qualquer momento";
  if (min < 60) return "em " + min + " min";
  const h = Math.floor(min / 60);
  const resto = min % 60;
  return "em " + h + " h" + (resto ? " " + resto + " min" : "");
}

const ROTULO_STATUS: Record<string, string> = {
  ok: "Completa",
  parcial: "Incompleta",
  falhou: "Falhou",
  rodando: "Em andamento",
};

/* ------------------------------------------------------------------ *
 *  Pedaços da tela (fora do componente: definidos dentro, remontam a    *
 *  cada tecla digitada e a página salta — já aconteceu aqui antes)      *
 * ------------------------------------------------------------------ */

function Selo({ status }: { status: string }) {
  return (
    <span className={"bk-selo bk-selo-" + status}>
      {status === "ok" ? <IconCheck size={13} /> : status === "rodando" ? <IconClock size={13} /> : <IconAlert size={13} />}
      {ROTULO_STATUS[status] ?? status}
    </span>
  );
}

function LinhaCopia({
  copia,
  aberta,
  onAbrir,
  onBaixar,
  onRemover,
}: {
  copia: Copia;
  aberta: boolean;
  onAbrir: () => void;
  onBaixar: (parte?: string) => void;
  onRemover: () => void;
}) {
  const partes = Array.isArray(copia.partes) ? copia.partes : [];
  const falhas = partes.filter((p) => !p.ok).length;
  const apagada = Boolean(copia.removido_em);
  return (
    <li className={"bk-copia" + (apagada ? " bk-copia-apagada" : "")}>
      <button className="bk-copia-head" onClick={onAbrir} aria-expanded={aberta}>
        <span className="bk-copia-quando">
          <strong>{quando(copia.iniciado_em)}</strong>
          <span className="muted">{relativo(copia.iniciado_em)}</span>
        </span>
        <Selo status={copia.status} />
        <span className="bk-copia-tam">{bytes(copia.tamanho_bytes)}</span>
        <span className="muted bk-copia-origem">
          {copia.origem === "manual" ? "feita à mão" + (copia.criado_por ? " · " + copia.criado_por : "") : "automática"}
        </span>
        {apagada ? (
          <span className="muted">
            {copia.removido_por ? "apagada por " + copia.removido_por : "apagada pela regra de guarda"}
          </span>
        ) : (
          <span className="muted">{partes.length} arquivo(s){falhas ? " · " + falhas + " com erro" : ""}</span>
        )}
      </button>

      {aberta && (
        <div className="bk-copia-corpo">
          {copia.erro && <p className="bk-erro">{copia.erro}</p>}
          {apagada ? (
            <p className="muted">
              Os arquivos desta cópia foram apagados em {quando(copia.removido_em)}
              {copia.removido_por
                ? " por " + copia.removido_por + "."
                : " pela regra de guarda, para não encher o disco."}{" "}
              O registro fica aqui para o histórico não ter buraco.
            </p>
          ) : (
            <>
              <ul className="bk-partes">
                {partes.map((p) => (
                  <li key={p.nome} className={p.ok ? "" : "bk-parte-erro"}>
                    <span className="bk-parte-icone">
                      {p.tipo === "banco" ? <IconDatabase size={15} /> : p.tipo === "config" ? <IconLock size={15} /> : <IconFolder size={15} />}
                    </span>
                    <span className="bk-parte-nome">{p.nome}</span>
                    {p.ok ? (
                      <>
                        <span className="muted">{bytes(p.bytes)}</span>
                        <button className="btn-link" onClick={() => onBaixar(p.arquivo)}>
                          baixar
                        </button>
                      </>
                    ) : (
                      <span className="bk-parte-msg">{p.erro ?? "não salvou"}</span>
                    )}
                  </li>
                ))}
              </ul>
              {copia.email_status && <p className="muted">E-mail: {copia.email_status}</p>}
              {copia.pasta && <p className="bk-caminho">{copia.pasta}</p>}
              <div className="bk-copia-acoes">
                <button className="btn-secondary btn-sm" onClick={() => onBaixar()}>
                  <IconDownload size={15} /> Baixar a cópia inteira
                </button>
                <button className="btn-link bk-apagar" onClick={onRemover}>
                  <IconTrash size={14} /> Apagar esta cópia
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}

/* ------------------------------------------------------------------ *
 *  A TELA                                                              *
 * ------------------------------------------------------------------ */

export default function BackupView() {
  const [retrato, setRetrato] = useState<Retrato | null>(null);
  const [alvos, setAlvos] = useState<Alvos | null>(null);
  const [copias, setCopias] = useState<Copia[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [rodando, setRodando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [aberta, setAberta] = useState<number | null>(null);
  const [configAberta, setConfigAberta] = useState(false);
  const [form, setForm] = useState<Config | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [r, a, lista] = await Promise.all([
        api.backup.config() as Promise<Retrato>,
        api.backup.alvos() as Promise<Alvos>,
        api.backup.listar() as Promise<Copia[]>,
      ]);
      setRetrato(r);
      setAlvos(a);
      setCopias(lista);
      setErro(null);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível ler os backups.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Enquanto uma cópia está sendo feita, a tela se atualiza sozinha: um
  // pg_dump de sete bancos leva minutos e ninguém deve ficar apertando F5.
  useEffect(() => {
    if (!rodando && !retrato?.rodando) return;
    const id = setInterval(carregar, 5000);
    return () => clearInterval(id);
  }, [rodando, retrato?.rodando, carregar]);

  async function fazerAgora() {
    setRodando(true);
    setAviso(null);
    setErro(null);
    try {
      const r = (await api.backup.rodarAgora()) as { status: string; erro?: string };
      setAviso(
        r.status === "ok"
          ? "Cópia concluída."
          : r.status === "parcial"
            ? "Cópia feita, mas um pedaço não salvou — veja o detalhe abaixo."
            : "A cópia não saiu: " + (r.erro ?? "erro desconhecido"),
      );
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível fazer a cópia agora.");
    } finally {
      setRodando(false);
      carregar();
    }
  }

  function baixar(id: number, parte?: string) {
    window.open(api.backup.urlBaixar(id, parte), "_blank", "noopener");
  }

  async function remover(id: number) {
    if (!confirm("Apagar esta cópia de segurança do servidor? Não dá para desfazer.")) return;
    try {
      await api.backup.remover(id);
      carregar();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível apagar.");
    }
  }

  function abrirConfig() {
    if (retrato) setForm({ ...retrato.config });
    setConfigAberta(true);
  }

  async function salvarConfig() {
    if (!form) return;
    setSalvando(true);
    setErro(null);
    try {
      await api.backup.salvar(form as unknown as Record<string, unknown>);
      setConfigAberta(false);
      setAviso("Configuração salva. Vale a partir da próxima cópia.");
      carregar();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  const campo =
    <K extends keyof Config>(chave: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const alvo = e.target as HTMLInputElement;
      const valor =
        alvo.type === "checkbox" ? alvo.checked : alvo.type === "number" ? Number(alvo.value) : alvo.value;
      setForm((f) => (f ? { ...f, [chave]: valor as Config[K] } : f));
    };

  if (carregando) return <p className="aud-vazio">Carregando…</p>;

  const c = retrato?.config;
  const emAndamento = rodando || Boolean(retrato?.rodando);

  return (
    <div className="bk">
      {erro && <p className="form-error">{erro}</p>}
      {aviso && (
        <div className="dev-toast" role="status">
          <span>{aviso}</span>
          <button className="btn-link" onClick={() => setAviso(null)}>
            fechar
          </button>
        </div>
      )}

      {/* ---------- COMO ESTÁ ---------- */}
      <div className="aud-dossie-num aud-num-texto">
        <span className="aud-num">
          <b>{retrato?.ultimo ? quando(retrato.ultimo.iniciado_em) : "nenhuma ainda"}</b>
          <span>última cópia {retrato?.ultimo ? "· " + relativo(retrato.ultimo.iniciado_em) : ""}</span>
        </span>
        <span className="aud-num">
          <b>{c?.ativo ? "a cada " + c.cadaHoras + " h" : "desligado"}</b>
          <span>{c?.ativo ? "próxima " + daqui(retrato?.proxima ?? null) : "as cópias automáticas estão paradas"}</span>
        </span>
        <span className="aud-num">
          <b>{bytes(retrato?.guardado.bytes ?? 0)}</b>
          <span>{retrato?.guardado.copias ?? 0} cópia(s) guardada(s)</span>
        </span>
        <span className="aud-num">
          <b>{retrato?.ultimo ? (ROTULO_STATUS[retrato.ultimo.status] ?? retrato.ultimo.status) : "—"}</b>
          <span>como terminou a última</span>
        </span>
      </div>

      {c && !c.ativo && (
        <p className="bk-atencao">
          <IconAlert size={16} /> As cópias automáticas estão <strong>desligadas</strong>. Nada está
          sendo salvo sozinho.
        </p>
      )}
      {alvos && !alvos.pgDumpOk && c?.incluirBancos && (
        <p className="bk-atencao">
          <IconAlert size={16} /> O <strong>pg_dump não foi encontrado</strong> neste servidor — sem
          ele, os bancos não entram na cópia. Instale o cliente do PostgreSQL ou informe o caminho na
          configuração abaixo.
        </p>
      )}
      {retrato?.ultimo?.erro && (
        <p className="bk-atencao">
          <IconAlert size={16} /> Na última cópia: {retrato.ultimo.erro}
        </p>
      )}

      <div className="crud-toolbar">
        <div className="bk-onde">
          <IconFolder size={15} />
          <span className="bk-caminho">{c?.destino}</span>
        </div>
        <div className="toolbar-right">
          <button className="btn-secondary" onClick={abrirConfig}>
            <IconClock size={16} /> Configurar
          </button>
          <button className="btn-primary" onClick={fazerAgora} disabled={emAndamento}>
            <IconRefresh size={16} /> {emAndamento ? "Copiando…" : "Fazer cópia agora"}
          </button>
        </div>
      </div>

      {/* ---------- O QUE ENTRA ---------- */}
      <h4 className="aud-mini-titulo">O que entra na cópia</h4>
      <div className="bk-alvos">
        <div className="bk-alvo-bloco">
          <h5>
            <IconDatabase size={16} /> Bancos de dados
            <span className="muted">{alvos?.bancos.length ?? 0}</span>
          </h5>
          {c?.incluirBancos ? (
            <ul className="bk-lista-bancos">
              {alvos?.bancos.map((b) => (
                <li key={b.nome}>
                  <strong>{b.nome}</strong>
                  <span className="muted">{b.sistemas.join(", ")}</span>
                </li>
              ))}
              {!alvos?.bancos.length && <li className="muted">Nenhum banco encontrado nos .env.</li>}
            </ul>
          ) : (
            <p className="muted">Desligado na configuração.</p>
          )}
        </div>

        <div className="bk-alvo-bloco">
          <h5>
            <IconFolder size={16} /> Documentos
            <span className="muted">{alvos?.pastas.length ?? 0}</span>
          </h5>
          {c?.incluirDocumentos ? (
            <ul>
              {alvos?.pastas.map((p) => (
                <li key={p.caminho}>
                  <strong>{p.sobre}</strong>
                  <span className="muted">{bytes(p.bytes)}</span>
                </li>
              ))}
              {Boolean(alvos?.comercial.length) && (
                <li>
                  <strong>Dados do Comercial (arquivos JSON)</strong>
                  <span className="muted">{alvos?.comercial.length} arquivo(s)</span>
                </li>
              )}
              {!alvos?.pastas.length && !alvos?.comercial.length && (
                <li className="muted">Nenhuma pasta de documento encontrada.</li>
              )}
            </ul>
          ) : (
            <p className="muted">Desligado na configuração.</p>
          )}
        </div>

        <div className="bk-alvo-bloco">
          <h5>
            <IconLock size={16} /> Configuração
          </h5>
          {c?.incluirConfig ? (
            <p className="muted">
              Os <code>.env</code> de cada sistema, a chave do e-mail e o <code>ecosystem.config.cjs</code> —
              é com isso que a suíte se reinstala do zero. Tem senha dentro: <strong>nunca</strong> é
              anexada em e-mail.
            </p>
          ) : (
            <p className="muted">Desligado na configuração.</p>
          )}
        </div>
      </div>

      {/* ---------- A CONFIGURAÇÃO ---------- */}
      {configAberta && form && (
        <div className="form-card">
          <div className="form-grid">
            <label className="dev-check field-wide">
              <input type="checkbox" checked={form.ativo} onChange={campo("ativo")} />
              Fazer cópias automáticas
            </label>

            <label className="field">
              <span>De quantas em quantas horas</span>
              <input type="number" min={1} max={24} value={form.cadaHoras} onChange={campo("cadaHoras")} />
            </label>
            <label className="field field-wide">
              <span>Onde guardar</span>
              <input
                value={form.destino}
                onChange={campo("destino")}
                placeholder={retrato?.destinoPadrao}
              />
              <small className="hint">
                Fora da pasta da instalação ({retrato?.instalacao}) — um deploy que substitui a pasta
                não pode levar os backups junto.
              </small>
            </label>

            <label className="dev-check field-wide">
              <input type="checkbox" checked={form.incluirBancos} onChange={campo("incluirBancos")} />
              Incluir os bancos de dados
            </label>
            <label className="dev-check field-wide">
              <input type="checkbox" checked={form.incluirDocumentos} onChange={campo("incluirDocumentos")} />
              Incluir os documentos e os arquivos do Comercial
            </label>
            <label className="dev-check field-wide">
              <input type="checkbox" checked={form.incluirConfig} onChange={campo("incluirConfig")} />
              Incluir a configuração (.env e chaves) — contém senhas
            </label>

            <label className="field field-wide">
              <span>Pastas extras (uma por linha)</span>
              <textarea
                rows={2}
                value={form.pastasExtras}
                onChange={campo("pastasExtras")}
                placeholder="Ex.: sistemaX/arquivos_novos"
              />
            </label>

            <label className="field">
              <span>Guardar as últimas</span>
              <input type="number" min={1} max={200} value={form.manterRecentes} onChange={campo("manterRecentes")} />
              <small className="hint">cópias recentes</small>
            </label>
            <label className="field">
              <span>Uma por dia, por</span>
              <input type="number" min={0} max={365} value={form.manterDias} onChange={campo("manterDias")} />
              <small className="hint">dias</small>
            </label>
            <label className="field">
              <span>Uma por semana, por</span>
              <input type="number" min={0} max={200} value={form.manterSemanas} onChange={campo("manterSemanas")} />
              <small className="hint">semanas</small>
            </label>

            <label className="field">
              <span>Mandar por e-mail</span>
              <select value={form.emailQuando} onChange={campo("emailQuando")}>
                <option value="nunca">Nunca</option>
                <option value="diario">Uma vez por dia</option>
                <option value="sempre">Em toda cópia</option>
              </select>
            </label>
            <label className="field">
              <span>A partir das</span>
              <input type="time" value={form.emailHora} onChange={campo("emailHora")} />
            </label>
            <label className="field field-wide">
              <span>Para quem</span>
              <input
                value={form.emailPara}
                onChange={campo("emailPara")}
                placeholder="email@empresa.com, outro@empresa.com"
              />
              <small className="hint">
                Usa o servidor de e-mail configurado na aba Lembretes.
              </small>
            </label>
            <label className="dev-check field-wide">
              <input type="checkbox" checked={form.emailAnexar} onChange={campo("emailAnexar")} />
              Anexar os arquivos da cópia quando couberem
            </label>
            <label className="field">
              <span>Tamanho máximo do e-mail (MB)</span>
              <input type="number" min={1} max={100} value={form.emailLimiteMb} onChange={campo("emailLimiteMb")} />
              <small className="hint">
                Já contando o inchaço do anexo. A maioria dos servidores corta em 25 MB.
              </small>
            </label>
            <label className="dev-check field-wide">
              <input type="checkbox" checked={form.emailFalha} onChange={campo("emailFalha")} />
              Avisar por e-mail quando uma cópia falhar
            </label>

            <label className="field field-wide">
              <span>Caminho do pg_dump (opcional)</span>
              <input
                value={form.pgDump}
                onChange={campo("pgDump")}
                placeholder={alvos?.pgDump ?? "Ex.: /usr/lib/postgresql/17/bin/pg_dump"}
              />
              <small className="hint">
                Só é preciso quando o pg_dump não está no PATH, ou quando há mais de uma versão
                instalada — a versão tem de ser a mesma do servidor do banco.
              </small>
            </label>
          </div>
          <div className="solicitar-foot">
            <button className="btn-secondary btn-sm" onClick={() => setConfigAberta(false)} disabled={salvando}>
              Cancelar
            </button>
            <button className="btn-primary btn-sm" onClick={salvarConfig} disabled={salvando}>
              {salvando ? "Salvando…" : "Salvar configuração"}
            </button>
          </div>
          {form.atualizadoEm && (
            <p className="muted bk-rodape-config">
              Última mudança: {quando(form.atualizadoEm)}
              {form.atualizadoPor ? " por " + form.atualizadoPor : ""}
            </p>
          )}
        </div>
      )}

      {/* ---------- O HISTÓRICO ---------- */}
      <h4 className="aud-mini-titulo">Cópias feitas</h4>
      {copias.length === 0 ? (
        <p className="empty">
          Nenhuma cópia ainda. A primeira sai sozinha na próxima virada de {c?.cadaHoras ?? 3} horas —
          ou clique em “Fazer cópia agora”.
        </p>
      ) : (
        <ul className="bk-copias">
          {copias.map((copia) => (
            <LinhaCopia
              key={copia.id}
              copia={copia}
              aberta={aberta === copia.id}
              onAbrir={() => setAberta(aberta === copia.id ? null : copia.id)}
              onBaixar={(parte) => baixar(copia.id, parte)}
              onRemover={() => remover(copia.id)}
            />
          ))}
        </ul>
      )}

      {/* ---------- COMO VOLTAR ATRÁS ---------- */}
      <h4 className="aud-mini-titulo">Como restaurar</h4>
      <div className="bk-restaurar">
        <p>
          <IconShieldCheck size={16} /> Restaurar não tem botão de propósito: um clique errado apaga
          o banco de produção. Cada cópia leva um <code>LEIA-ME.txt</code> com os comandos prontos,
          e o caminho é sempre este:
        </p>
        <ol>
          <li>
            Pare o portal: <code>pm2 stop sercamp-portal</code>
          </li>
          <li>
            Banco: <code>pg_restore --clean --if-exists --no-owner -d sercamp banco-sercamp.dump</code>
          </li>
          <li>
            Documentos: <code>tar -xzf docs-colaboradores.tar.gz -C {retrato?.instalacao ?? "/root/portalsercamp"}</code>
          </li>
          <li>
            Suba de novo: <code>pm2 start sercamp-portal</code>
          </li>
        </ol>
        <p className="muted">
          <IconMail size={14} /> A cópia que chega por e-mail é a mesma — os anexos são estes
          arquivos.
        </p>
      </div>
    </div>
  );
}
